<template>
	<div class="content-page content-page--integrations-manager">
		<ActionSetNav
			:display="true"
			:name="pageTitle"
			:actions="actionNavActions"
		/>

		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title" data-test="title">
					3P Vendor Integration Manager
					<span class="content-page__title--detail"
						>- {{ pageTitle }}</span
					>
				</h2>
			</div>
			<div class="content-page__actions">
				<MenuButton
					variantName="primary"
					:items="addIntegrationOptions"
					v-model="addIntegrationValue"
					@change="addIntegration"
					v-if="validatePermission('vendors.create')"
				/>
			</div>
		</div>

		<div
			v-if="!loading"
			class="results-toolbar results-toolbar--variant-seven"
			data-test="resultsToolBar"
		>
			<div class="results-toolbar__search">
				<Search
					variant="toggle-icons"
					placeholder="Search by integration name, alias, CCL or API"
					v-model="filters.search"
				/>
			</div>
			<div class="results-toolbar__sort">
				<DropdownSort
					:options="ddsOptions"
					:showClearSelectedButton="false"
					v-model="ddsValue"
				/>
			</div>
			<div class="results-toolbar__filter">
				<Dropdown
					name="status"
					defaultValue="All Status"
					:options="ddStatusOptions"
					:showClearSelectedButton="false"
					v-model="filters.status"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter :counter="integrationsList.length" />
			</div>
		</div>
		<Spinner :processor="{ loading: loading }" />

		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<IntegrationsList
				:source="integrationsList"
				@displayApisModal="handleDisplayApisModal"
				ref="integrationsList"
			/>
		</ScrollContainer>
		<NoResults v-show="showNoResult" data-test="noResults" />
		<ApisModal
			:display="displayApisModal"
			:integration="integration"
			@close="() => (displayApisModal = false)"
		/>
	</div>
</template>

<script>
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import Search from '@/components/forms/search/search';
import DropdownSort from '@/components/forms/dropdown/dropdown-sort';
import Dropdown from '@/components/forms/dropdown/dropdown';
import ResultCounter from '@/site-components/result-counter/result-counter';
import IntegrationsList from './integrations-summary-list';
import Spinner from '@/components/visual/spinner/spinner';
import MenuButton from '@/components/forms/button/menu-button';
import NoResults from '@/site-components/no-results/no-results';
import ApisModal from '@/sections/vendors/apis/apis-modal';
import PageKeyNames from '@/utils/page-key-names';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import { mapActions, mapGetters } from 'vuex';
import {
	INTEGRATIONS,
	SET_INTEGRATIONS,
	LOADING
} from '@/sections/vendors/integrations/integrations-mutation-types';
import _ from 'lodash';

export default {
	name: 'IntegrationsSummaryPage',
	components: {
		ActionSetNav,
		Search,
		DropdownSort,
		Dropdown,
		ResultCounter,
		Breadcrumbs,
		IntegrationsList,
		Spinner,
		MenuButton,
		NoResults,
		ApisModal,
		ScrollContainer
	},
	props: {
		integratorId: {
			type: Number,
			required: true
		},
		pageTitle: {
			type: String,
			default: 'pageTitle' // TODO: remove after the api return page title
		},
		search: {
			type: String
		},
		sortby: {
			type: String
		},
		sortdirection: {
			type: String
		},
		status: {
			type: String
		},
		count: {
			type: Number
		},
		page: {
			type: Number
		}
	},
	data() {
		return {
			actionNavActions: [
				{
					id: 1,
					disabled: false,
					iconClass: 'icon icon-document',
					label: 'Details',
					title: 'Select to go to Details',
					type: 'route-link',
					visible: true
				},
				{
					id: 2,
					disabled: false,
					iconClass: 'icon icon-company',
					label: 'CCL',
					title: 'Select to go to Company Customer Location',
					type: 'route-link',
					visible: true
				},
				{
					id: 3,
					disabled: false,
					iconClass: 'icon icon-customer-integration',
					label: 'Integrations',
					title: 'Select to go to Integrations',
					type: 'route-link',
					visible: true,
					page: PageKeyNames.INTEGRATION_MANAGER
				},
				{
					id: 4,
					action: '#',
					disabled: false,
					iconClass: 'icon icon-menu',
					label: '',
					title: 'select to see the more actions',
					type: 'dropdown',
					visible: true,
					items: [
						{
							id: 5,
							// action: {
							// 	path: `/vendors/${this.integratorId}/integrations/history/`
							// },
							disabled: false,
							iconClass: 'icon icon-history',
							label: 'History',
							title: 'Select to go to History',
							type: 'route-link',
							visible: true
						}
					]
				}
			],
			addIntegrationValue: {
				text: 'Add Integration',
				value: 'default'
			},
			addIntegrationOptions: [
				{
					text: 'SecureVUE',
					value: 'SecureVUE'
				},
				{
					text: 'Data Extract',
					value: 'DataExtract'
				},
				{
					text: 'OEM',
					value: 'OEM'
				}
			],
			displayApisModal: false,
			integration: {},
			ddStatusOptions: [
				{
					text: 'All Status',
					value: null
				},
				{
					text: 'Active',
					value: 'Active'
				},
				{
					text: 'Inactive',
					value: 'Inactive'
				},
				{
					text: 'Errors',
					value: 'Errors'
				}
			],
			ddsOptions: [
				{ text: 'Name', value: 'name' },
				{ text: 'Alias', value: 'alias' },
				{ text: 'Type', value: 'type' },
				{ text: 'ID', value: 'id' }
			],
			apiCalled: false,
			filters: {
				search: this.search,
				sortby: this.sortby,
				sortdirection: this.sortdirection,
				status: this.status,
				page: this.page || 1,
				count: this.count
			},
			defaultFilters: {
				search: '',
				sortby: 'name',
				sortdirection: 'asc',
				page: 1,
				count: 100
			}
		};
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.INTEGRATION_MANAGER);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	computed: {
		...mapGetters('vendorIntegrations', [INTEGRATIONS, LOADING]),

		ddsValue: {
			get() {
				return {
					sortby: this.filters.sortby,
					sortdirection: this.filters.sortdirection
				};
			},
			set(value) {
				this.filters.sortby = value.sortby;
				this.filters.sortdirection = value.sortdirection;
			}
		},
		urlNames() {
			return [
				{
					name: '3P Vendor Manager',
					url: '/vendors'
				}
			];
		},
		integrationsList() {
			return this[INTEGRATIONS].items || [];
		},
		hasResults() {
			return this.integrationsList.length && !this.loading;
		},
		showNoResult() {
			return (
				!this[INTEGRATIONS].totalCount &&
				!this[LOADING] &&
				this.apiCalled
			);
		},
		hasMoreItemsToLoad() {
			return _.isEmpty(this[INTEGRATIONS])
				? false
				: this[INTEGRATIONS].items?.length <
						this[INTEGRATIONS].totalCount;
		}
	},
	methods: {
		...mapActions('vendorIntegrations', {
			fetchIntegrations: SET_INTEGRATIONS
		}),
		addIntegration(o) {
			if (o.value === 'SecureVUE') {
				this.addNewIntegration();
			}
		},
		async fetchData() {
			this.apiCalled = true;
			let params = { ...this.filters };
			Object.assign(params, { integratorId: this.integratorId });
			if (params.status === 'all') params.status = null;
			await this.fetchIntegrations(params);
		},
		handleDisplayApisModal(integrationObj) {
			this.integration = integrationObj;
			this.displayApisModal = true;
		},
		addNewIntegration() {
			this.$router.push({
				path: `/vendors/${this.integratorId}/integrations/add/securevue`
			});
		},
		async updateRoute(queries) {
			this.$router.push({
				path: `/vendors/${this.integratorId}/integrations`,
				query: {
					search: queries.search,
					sortBy: queries.sortby,
					sortDirection: queries.sortdirection,
					status: queries.status,
					page: queries.page,
					count: queries.count
				}
			});

			await this.fetchData();
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.filters.count = _.clamp(
					this.filters.count + this.defaultFilters.count,
					this[INTEGRATIONS].totalCount
				);
			}
		}
	},
	watch: {
		filters: {
			deep: true,
			immediate: true,
			async handler(newValue) {
				await this.updateRoute(newValue);
			}
		},

		'$route.query': {
			handler(newQuery) {
				if (_.isEmpty(newQuery)) {
					this.filters = _.cloneDeep(this.defaultFilters);
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		if (to.name !== 'vendors') {
			let form = { ...this.urlNames[0] };
			form.name = '3P Vendor Manager';

			to.meta.breadcrumb = [
				form,
				{
					name: '3P Vendor Integration Manager',
					data: { path: from.path }
				}
			];
		}
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
	* 3P Vendor Integration Manager
	* Can use SCSS variables = TRUE
    * This should only contain classes and styles used for the page layout.
*/

::v-deep .results-toolbar .results-toolbar__search {
	@include mq($from: md) {
		width: 352px;
	}
}

::v-deep .content-page__actions .dropdown__list-container {
	left: initial;
	right: 0;
}
</style>
